/**
 * Partner filtering and fetching hook.
 *
 * Manages partner search and filtering functionality. Fetches partner requests
 * from the API, applies client-side filters (level, goal, location, availability),
 * and filters out the current user's own requests.
 *
 * @module usePartnerFilters
 */

import { useMemo } from 'react';
import { PartnerSearchFilters, Partner, DayOfWeek } from '@/types/partner-finder';
import { getAllPartnerRequests } from '@/lib/partnerApi';
import { mapApiRequestToPartner } from '@/lib/mappers';
import { useAsyncData } from './useAsyncData';
import { useTranslation } from 'react-i18next';
import {
  translateMeetingPreference,
  translatePartnerDays,
  translatePartnerGoal,
  translatePartnerLocation,
} from '@/lib/partnerFinderLabels';

/**
 * Helper function to check if partner has matching availability.
 *
 * @param partner - Partner object to check
 * @param filterDays - Set of days to match against
 * @returns True if partner has at least one matching availability day
 */
function hasMatchingAvailability(partner: Partner, filterDays: Set<DayOfWeek>): boolean {
  return partner.availability?.some((day) => filterDays.has(day)) ?? false;
}

/**
 * Custom hook for managing partner filtering and fetching.
 *
 * Fetches partner requests from the API, applies client-side filters (level, goal,
 * location, availability, keyword), and filters out the current user's own requests.
 * Combines API filtering with client-side filtering for optimal results.
 *
 * @param filters - Current filter state including level, goal, location, meeting preference, availability, and keyword
 * @param currentUserId - Current user ID to filter out own requests (optional)
 * @returns Object containing:
 *   - `partners`: Array of filtered Partner objects
 *   - `isLoading`: Boolean indicating if data is being fetched
 *
 * @example
 * ```typescript
 * const { partners, isLoading } = usePartnerFilters(filters, currentUserId);
 * ```
 */
export function usePartnerFilters(filters: PartnerSearchFilters, currentUserId?: string) {
  // Fetch partners from API using useAsyncData
  const { data: partners, isLoading } = useAsyncData<Partner[]>(
    async () => {
      // Map filters to API format - only include defined values
      const apiFilters: {
        level?: string;
        goal?: string;
        isOnline?: boolean;
        location?: string;
        searchQuery?: string;
      } = {};

      if (filters.level) apiFilters.level = filters.level;
      if (filters.goal) apiFilters.goal = filters.goal;
      if (filters.meetingPreference === 'online') apiFilters.isOnline = true;
      if (filters.meetingPreference === 'in-person') apiFilters.isOnline = false;
      if (filters.location) apiFilters.location = filters.location;
      const trimmedKeyword = filters.keyword?.trim();
      if (trimmedKeyword) apiFilters.searchQuery = trimmedKeyword;

      const apiRequests = await getAllPartnerRequests(apiFilters);

      // Map to Partner type
      const mappedPartners = apiRequests.map((req) => mapApiRequestToPartner(req));

      // Filter out current user's requests
      const filteredPartners = currentUserId
        ? mappedPartners.filter((partner) => partner.userId !== currentUserId)
        : mappedPartners;

      // Apply client-side availability filter (API doesn't support it)
      let filtered = filteredPartners;
      if (filters.availability && filters.availability.length > 0) {
        const filterDays = new Set(filters.availability);
        filtered = filtered.filter((partner) => hasMatchingAvailability(partner, filterDays));
      }

      // Apply keyword filter client-side as additional refinement
      // (API search might not cover all fields, so we do client-side filtering too)
      const keyword = filters.keyword?.trim()?.toLowerCase();
      if (keyword) {
        filtered = filtered.filter(
          (partner) =>
            partner.displayName.toLowerCase().includes(keyword) ||
            partner.about?.toLowerCase().includes(keyword) ||
            partner.goal.toLowerCase().includes(keyword),
        );
      }

      return filtered;
    },
    [filters, currentUserId],
  );

  return { partners: partners ?? [], isLoading };
}

/**
 * Custom hook for managing active filters display.
 *
 * Processes the current filter state and returns an array of active filters
 * formatted for display in the UI. Used to show which filters are currently applied.
 *
 * @param filters - Current filter state
 * @returns Array of active filter objects, each containing:
 *   - `key`: Filter key identifier
 *   - `label`: Display label for the filter
 *   - `value`: Display value for the filter
 *
 * @example
 * ```typescript
 * const activeFilters = useActiveFilters(filters);
 * activeFilters.forEach(filter => {
 *   console.log(`${filter.label}: ${filter.value}`);
 * });
 * ```
 */
export function useActiveFilters(filters: PartnerSearchFilters) {
  const { t } = useTranslation();
  return useMemo(() => {
    const active: Array<{ key: string; label: string; value: string }> = [];
    if (filters.keyword)
      active.push({ key: 'keyword', label: filters.keyword, value: filters.keyword });
    if (filters.level) active.push({ key: 'level', label: t('partnerFinder.search.levelLabel', { defaultValue: 'Level' }), value: filters.level });
    if (filters.goal) active.push({ key: 'goal', label: t('partnerFinder.search.goalLabel', { defaultValue: 'Goal' }), value: translatePartnerGoal(t, filters.goal) });
    if (filters.location)
      active.push({ key: 'location', label: t('partnerFinder.search.locationLabel', { defaultValue: 'Location' }), value: translatePartnerLocation(t, filters.location) });
    if (filters.meetingPreference)
      active.push({
        key: 'meetingPreference',
        label: t('partnerFinder.search.meetingShortLabel', { defaultValue: 'Meeting' }),
        value: translateMeetingPreference(t, filters.meetingPreference),
      });
    if (filters.availability && filters.availability.length > 0)
      active.push({
        key: 'availability',
        label: t('partnerFinder.search.daysLabel', { defaultValue: 'Days' }),
        value: translatePartnerDays(t, filters.availability),
      });
    return active;
  }, [filters, t]);
}
